import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { ThemeProvider } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import DinnerDiningOutlinedIcon from '@mui/icons-material/DinnerDiningOutlined';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';

import SectionAccordion from './SectionAccordion';
import AllergenChipGrid from './AllergenChipGrid';
import { menuFormTheme, textInputCss } from '../utils/form-styles';
import { BTN_COLOR, TEXT_SECONDARY_COLOR, THEME_COLOR4, THEME_COLOR5 } from '../../../../../constants';
import type { TMenuItemFormValues } from '../schemas/menu-item-schema';
import type { TAllergen, TAttribute } from '../schemas';

type TDietarySectionProps = {
  accordionRef: React.RefObject<HTMLElement>;
  allergensList: TAllergen[];
  attributesList: TAttribute[];
  expanded?: boolean;
  iconColor: string;
  headerBackground: string;
  onToggle?: (expanded: boolean) => void;
};

const DietarySection = ({
  accordionRef,
  allergensList,
  attributesList,
  expanded,
  iconColor,
  headerBackground,
  onToggle,
}: TDietarySectionProps) => {
  const {
    register,
    control,
    watch,
    setValue,
    formState: { errors, isSubmitted },
  } = useFormContext<TMenuItemFormValues>();

  const hasError = isSubmitted && !!errors.calories;

  const selectedAttributes = watch('attributes') ?? [];

  return (
    <SectionAccordion
      accordionRef={accordionRef}
      title="Dietary & nutrition"
      subtitle="Allergens, attributes, calories"
      icon={<DinnerDiningOutlinedIcon fontSize="small" />}
      iconColor={iconColor}
      headerBackground={headerBackground}
      hasError={hasError}
      expanded={expanded}
      onToggle={onToggle}
    >
      <Grid container spacing={2.5}>
        {/* Allergens */}
        <Grid item xs={12}>
          <Typography style={labelStyle}>Allergens</Typography>
          <Typography sx={s.hint}>
            Select all that apply — these show as warnings to customers.
          </Typography>
          <Controller
            control={control}
            name="_idAllergens"
            render={({ field }) => (
              <AllergenChipGrid
                allergens={allergensList}
                value={field.value ?? []}
                onChange={field.onChange}
              />
            )}
          />
        </Grid>

        {/* Attributes multi-select */}
        <Grid item xs={12}>
          <Typography style={labelStyle}>Menu Attributes</Typography>
          <Controller
            control={control}
            name="attributes"
            render={({ field }) => {
              const selectedIds = (field.value ?? []).map((a) => a._idAttribute);
              return (
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  options={attributesList}
                  getOptionLabel={(option) => option.name}
                  value={attributesList.filter((a) => selectedIds.includes(a._id))}
                  onChange={(_, newValue) => {
                    field.onChange(
                      newValue.map(
                        (attr) =>
                          (field.value ?? []).find((v) => v._idAttribute === attr._id) ?? {
                            _idAttribute: attr._id,
                            value: '',
                          }
                      )
                    );
                  }}
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox
                        icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                        checkedIcon={<CheckBoxIcon fontSize="small" />}
                        style={{ marginRight: 8 }}
                        checked={selected}
                        sx={{ color: THEME_COLOR4, '&.Mui-checked': { color: BTN_COLOR } }}
                      />
                      <Typography sx={{ fontFamily: 'GilroyRegular', fontSize: 13 }}>
                        {option.name}
                      </Typography>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      placeholder="Select attributes..."
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                    />
                  )}
                  sx={{ mt: 1 }}
                />
              );
            }}
          />

          {/* Attribute value inputs */}
          {selectedAttributes.length > 0 && (
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {selectedAttributes.map((attr) => {
                const attrDetail = attributesList.find((a) => a._id === attr._idAttribute);
                const asset = attrDetail?.image || attrDetail?.icon || '';
                return (
                  <Box key={attr._idAttribute} sx={s.attributeRow}>
                    <Box sx={s.attributeLabel}>
                      {asset ? (
                        <Box
                          component="img"
                          src={asset}
                          alt={attrDetail?.name}
                          sx={{ width: 20, height: 20, objectFit: 'contain', flexShrink: 0 }}
                        />
                      ) : (
                        <Box sx={s.attributeInitial}>
                          {(attrDetail?.name ?? 'A').charAt(0).toUpperCase()}
                        </Box>
                      )}
                      <Typography sx={s.attributeName} noWrap>
                        {attrDetail?.name ?? 'Unknown'}
                      </Typography>
                    </Box>
                    <TextField
                      size="small"
                      placeholder="value (optional)"
                      value={attr.value}
                      onChange={(e) =>
                        setValue(
                          'attributes',
                          selectedAttributes.map((a) =>
                            a._idAttribute === attr._idAttribute
                              ? { ...a, value: e.target.value }
                              : a
                          ),
                          { shouldDirty: true }
                        )
                      }
                      sx={{ width: 160, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                    />
                    <IconButton
                      size="small"
                      onClick={() =>
                        setValue(
                          'attributes',
                          selectedAttributes.filter(
                            (a) => a._idAttribute !== attr._idAttribute
                          ),
                          { shouldDirty: true }
                        )
                      }
                      aria-label="Remove attribute"
                    >
                      <DeleteIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                );
              })}
            </Box>
          )}
        </Grid>

        {/* Legacy allergies text */}
        <Grid item xs={12}>
          <ThemeProvider theme={menuFormTheme}>
            <TextField
              focused
              fullWidth
              multiline
              rows={2}
              id="allergies"
              label="Allergies (Legacy)"
              {...register('allergies')}
              variant="standard"
              sx={textInputCss}
              InputLabelProps={{ style: { color: THEME_COLOR5 }, sx: styles.inputLabel }}
              inputProps={{ autoComplete: 'none', sx: styles.inputText }}
            />
          </ThemeProvider>
        </Grid>

        {/* Calories */}
        <Grid item xs={12} md={6}>
          <ThemeProvider theme={menuFormTheme}>
            <TextField
              focused
              fullWidth
              id="calories"
              label="Calories (kcal)"
              {...register('calories')}
              variant="standard"
              sx={textInputCss}
              error={Boolean(errors.calories)}
              helperText={errors.calories?.message}
              InputLabelProps={{ style: { color: THEME_COLOR5 }, sx: styles.inputLabel }}
              inputProps={{ autoComplete: 'none', sx: styles.inputText }}
            />
          </ThemeProvider>
        </Grid>

        {/* Nutritions */}
        <Grid item xs={12} md={6}>
          <ThemeProvider theme={menuFormTheme}>
            <TextField
              focused
              fullWidth
              id="nutritions"
              label="Nutrition's"
              {...register('nutritions')}
              variant="standard"
              sx={textInputCss}
              InputLabelProps={{ style: { color: THEME_COLOR5 }, sx: styles.inputLabel }}
              inputProps={{ autoComplete: 'none', sx: styles.inputText }}
            />
          </ThemeProvider>
        </Grid>

        {/* Dish notes */}
        <Grid item xs={12}>
          <ThemeProvider theme={menuFormTheme}>
            <TextField
              focused
              fullWidth
              id="dishnote"
              label="Dish Notes"
              {...register('dishnote')}
              variant="standard"
              sx={textInputCss}
              InputLabelProps={{ style: { color: THEME_COLOR5 }, sx: styles.inputLabel }}
              inputProps={{ autoComplete: 'none', sx: styles.inputText }}
            />
          </ThemeProvider>
        </Grid>
      </Grid>
    </SectionAccordion>
  );
};

export default DietarySection;

const labelStyle = { color: THEME_COLOR5, fontFamily: 'GilroyRegular', fontSize: 12 } as React.CSSProperties;

const s = {
  hint: { mt: 0, mb: 1.5, fontFamily: 'GilroyRegular', fontSize: 12, color: TEXT_SECONDARY_COLOR },
  attributeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
  },
  attributeLabel: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    borderRadius: '8px',
    border: `1px solid ${THEME_COLOR4}`,
    backgroundColor: '#FAFAFA',
    px: 1.5,
    py: 0.75,
    minWidth: 0,
  },
  attributeInitial: {
    width: 20,
    height: 20,
    borderRadius: '4px',
    backgroundColor: '#E5E5E5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 11,
    fontFamily: 'GilroyBold',
    flexShrink: 0,
  },
  attributeName: { fontFamily: 'GilroyRegular', fontSize: 12, color: '#000' },
};

const styles = {
  inputLabel: { fontFamily: 'GilroyRegular' },
  inputText: { fontSize: 13, fontFamily: 'GilroySemiBold', my: 0.8 },
};
